/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'
import { getBrands, getUserSizes, getSizeComparison, submitBrandSize, deleteBrandSize } from '../api'
import { useAuth } from '../context/AuthContext'
import SizeModal from '../components/SizeModal'
import './SizeGuide.css'

const SIZES = ['XS', 'S', 'M', 'L', 'XL']
const MAX_KNOWN_BRANDS = 3

const blockInvalidKeys = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault()
}

const CATEGORIES = [
    { value: 'shirt', label: 'Shirts' },
    { value: 'outerwear', label: 'Outerwear & Knitwear' },
    { value: 'bottoms', label: 'Bottoms' },
    { value: 'pants', label: 'Pants' },
    { value: 'shoe', label: 'Shoes' },
]

function MySizingRow({ brand, sizes, onUpdate, onRemove }) {
    const [confirmRemove, setConfirmRemove] = useState(false)
    const parts = []
    if (sizes.shirt_size) parts.push(`Shirts: ${sizes.shirt_size}`)
    if (sizes.outerwear_size) parts.push(`Outerwear & Knitwear: ${sizes.outerwear_size}`)
    if (sizes.bottoms_size) parts.push(`Bottoms: ${sizes.bottoms_size}`)
    if (sizes.pants_waist && sizes.pants_inseam) parts.push(`Pants: ${sizes.pants_waist}×${sizes.pants_inseam}`)
    if (sizes.shoe_size) parts.push(`Shoes: ${sizes.shoe_size}`)

    return (
        <div className="my-sizing-row">
            <span className="my-sizing-brand">{brand.name}</span>
            <span className="my-sizing-values">{parts.join(' · ')}</span>
            <div className="my-sizing-actions">
                {!confirmRemove ? (
                    <>
                        <button className="my-sizing-edit" onClick={onUpdate}>Update</button>
                        <button className="my-sizing-remove" onClick={() => setConfirmRemove(true)}>Remove</button>
                    </>
                ) : (
                    <>
                        <span className="my-sizing-confirm-text">Remove?</span>
                        <button className="my-sizing-confirm-yes" onClick={onRemove}>Yes</button>
                        <button className="my-sizing-confirm-no" onClick={() => setConfirmRemove(false)}>No</button>
                    </>
                )}
            </div>
        </div>
    )
}

function MySizingSearch({ allBrands, userSizes, onSelect }) {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState([])

    useEffect(() => {
        if (!query.trim()) { setResults([]); return }
        const q = query.toLowerCase()
        const matches = allBrands
            .filter(b => b.name.toLowerCase().includes(q))
            .slice(0, 8)
        setResults(matches)
    }, [query, allBrands])

    const handleSelect = (brand) => {
        onSelect(brand)
        setQuery('')
        setResults([])
    }

    return (
        <div className="my-sizing-search-wrapper">
            <input
                type="text"
                className="search-input"
                placeholder="Search or add a brand..."
                value={query}
                onChange={e => setQuery(e.target.value)}
            />
            {results.length > 0 && (
                <div className="size-search-dropdown">
                    {results.map(brand => {
                        const hasSizing = !!userSizes[brand.id]
                        return (
                            <div
                                key={brand.id}
                                className="size-search-result my-sizing-search-result"
                                onClick={() => handleSelect(brand)}
                            >
                                <span>{brand.name}</span>
                                <span className={`my-sizing-search-action ${hasSizing ? 'update' : 'add'}`}>
                                    {hasSizing ? 'Update' : '+ Add sizing'}
                                </span>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

function SizeGuide() {
    const { user } = useAuth()
    const [allBrands, setAllBrands] = useState([])
    const [userSizes, setUserSizes] = useState({})
    const [targetBrand, setTargetBrand] = useState(null)
    const [targetCategory, setTargetCategory] = useState('')
    const [targetSearch, setTargetSearch] = useState('')
    const [targetResults, setTargetResults] = useState([])
    const [knownBrands, setKnownBrands] = useState([])
    const [knownSearch, setKnownSearch] = useState('')
    const [knownResults, setKnownResults] = useState([])
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [hasCompared, setHasCompared] = useState(false)
    const [editingSizeFor, setEditingSizeFor] = useState(null)

    useEffect(() => {
        getBrands().then(b => setAllBrands(b || []))
    }, [])

    useEffect(() => {
        if (user) {
            getUserSizes(user.id).then(sizes => {
                const byBrand = {}
                sizes.forEach(s => { byBrand[s.brand_id] = s })
                setUserSizes(byBrand)
            })
        }
    }, [user])

    useEffect(() => {
        if (targetSearch.trim().length < 1) { setTargetResults([]); return }
        setTargetResults(
            allBrands.filter(b =>
                b.name.toLowerCase().includes(targetSearch.toLowerCase()) &&
                b.id !== targetBrand?.id
            ).slice(0, 6)
        )
    }, [targetSearch, allBrands])

    useEffect(() => {
        if (knownSearch.trim().length < 1) { setKnownResults([]); return }
        setKnownResults(
            allBrands.filter(b =>
                b.name.toLowerCase().includes(knownSearch.toLowerCase()) &&
                !knownBrands.find(k => k.brand.id === b.id) &&
                b.id !== targetBrand?.id
            ).slice(0, 6)
        )
    }, [knownSearch, allBrands, knownBrands, targetBrand])

    const getUserSizeForCategory = (sizeRow, cat) => {
        if (!cat || !sizeRow) return ''
        if (cat === 'shirt') return sizeRow.shirt_size || ''
        if (cat === 'outerwear') return sizeRow.outerwear_size || ''
        if (cat === 'bottoms') return sizeRow.bottoms_size || ''
        if (cat === 'pants') {
            if (sizeRow.pants_waist && sizeRow.pants_inseam)
                return `${sizeRow.pants_waist}x${sizeRow.pants_inseam}`
            return ''
        }
        if (cat === 'shoe') return sizeRow.shoe_size ? String(sizeRow.shoe_size) : ''
        return ''
    }

    const reloadUserSizes = () => {
        if (user) {
            getUserSizes(user.id).then(sizes => {
                const byBrand = {}
                sizes.forEach(s => { byBrand[s.brand_id] = s })
                setUserSizes(byBrand)
            })
        }
    }

    const autoSaveSize = async (brandId, size) => {
        if (!user || !targetCategory || !size) return
        const sizes = {}
        if (targetCategory === 'shirt') sizes.shirt_size = size
        else if (targetCategory === 'outerwear') sizes.outerwear_size = size
        else if (targetCategory === 'bottoms') sizes.bottoms_size = size
        else if (targetCategory === 'shoe') sizes.shoe_size = parseFloat(size)
        else if (targetCategory === 'pants') {
            const parts = size.split('x')
            if (parts[0] && parts[1]) {
                sizes.pants_waist = parseInt(parts[0])
                sizes.pants_inseam = parseInt(parts[1])
            } else return
        }
        try {
            await submitBrandSize(user.id, brandId, sizes)
            reloadUserSizes()
        } catch (e) {
            console.error('Auto-save size failed:', e)
        }
    }

    const resetResults = () => {
        setResults([])
        setHasCompared(false)
    }

    const selectTarget = (brand) => {
        setTargetBrand(brand)
        setTargetSearch('')
        setTargetResults([])
        resetResults()
    }

    const addKnownBrand = (brand) => {
        if (knownBrands.length >= MAX_KNOWN_BRANDS) return
        setKnownBrands(prev => [...prev, {
            brand,
            size: getUserSizeForCategory(userSizes[brand.id] || {}, targetCategory)
        }])
        setKnownSearch('')
        setKnownResults([])
        resetResults()
    }

    const removeKnownBrand = (brandId) => {
        setKnownBrands(prev => prev.filter(k => k.brand.id !== brandId))
        resetResults()
    }

    const updateKnownSize = (brandId, value) => {
        setKnownBrands(prev => prev.map(k =>
            k.brand.id === brandId ? { ...k, size: value } : k
        ))
        resetResults()
        // Auto-save if size is complete
        if (value) autoSaveSize(brandId, value)
    }

    const handleCategoryChange = (cat) => {
        setTargetCategory(cat)
        setKnownBrands(prev => prev.map(k => ({
            ...k,
            size: getUserSizeForCategory(userSizes[k.brand.id] || {}, cat)
        })))
        resetResults()
    }

    const canCompare = targetBrand && targetCategory && knownBrands.length > 0

    const handleCompare = async () => {
        if (!canCompare) return
        setLoading(true)
        setHasCompared(true)

        // For pants, only use size if both waist and inseam are present
        let userSize = knownBrands[0]?.size || ''
        if (targetCategory === 'pants') {
            const parts = userSize.split('x')
            userSize = (parts[0] && parts[1]) ? userSize : ''
        }

        const knownBrandIds = knownBrands.map(k => k.brand.id)
        const data = await getSizeComparison(targetBrand.id, knownBrandIds, targetCategory, userSize)
        setResults(data?.results || [])
        setLoading(false)
    }

    const brandNameById = (id) => allBrands.find(b => b.id === id)?.name || id

    const renderKnownSizeInput = (k) => {
        const { brand, size } = k
        if (targetCategory === 'shirt' || targetCategory === 'outerwear' || targetCategory === 'bottoms') {
            return (
                <div className="known-size-pills">
                    {SIZES.map(s => (
                        <button
                            key={s}
                            className={`known-size-pill ${size === s ? 'active' : ''}`}
                            onClick={() => updateKnownSize(brand.id, size === s ? '' : s)}
                        >{s}</button>
                    ))}
                </div>
            )
        }
        if (targetCategory === 'pants') {
            const parts = size ? size.split('x') : ['', '']
            const waist = parts[0] || ''
            const inseam = parts[1] || ''
            return (
                <div className="known-pants-inputs">
                    <input type="number" className="known-size-input" placeholder="Waist"
                        value={waist} min={20} max={50}
                        onKeyDown={blockInvalidKeys}
                        onChange={e => {
                            const w = e.target.value
                            const i = inseam
                            updateKnownSize(brand.id, w || i ? `${w}x${i}` : '')
                        }}
                    />
                    <span className="known-pants-sep">×</span>
                    <input type="number" className="known-size-input" placeholder="Inseam"
                        value={inseam} min={20} max={50}
                        onKeyDown={blockInvalidKeys}
                        onChange={e => {
                            const w = waist
                            const i = e.target.value
                            updateKnownSize(brand.id, w || i ? `${w}x${i}` : '')
                        }}
                    />
                </div>
            )
        }
        if (targetCategory === 'shoe') {
            return (
                <input type="number" className="known-size-input" placeholder="US size e.g. 10"
                    value={size || ''} min={1} max={20} step={0.5}
                    onKeyDown={blockInvalidKeys}
                    onChange={e => updateKnownSize(brand.id, e.target.value)}
                />
            )
        }
    }

    const renderMatrix = (result) => {
        if (!result.has_data || result.matrix.length === 0) {
            return <div className="size-matrix-empty">No data yet for {brandNameById(result.known_brand_id)} × {targetBrand.name}.</div>
        }
        return (
            <div className="size-matrix">
                <div className="size-matrix-header">
                    <span className="size-matrix-known-brand">{brandNameById(result.known_brand_id)}</span>
                    <span className="size-matrix-arrow">→</span>
                    <span className="size-matrix-target-brand">{targetBrand.name}</span>
                </div>
                {result.matrix.map(row => (
                    <div key={row.known_size} className={`size-matrix-row ${row.is_user_size ? 'user-size' : ''}`}>
                        <div className="size-matrix-known-cell">
                            <span className="size-matrix-known-label">{row.known_size}</span>
                            {row.low_sample && <span className="size-low-sample">Small sample</span>}
                        </div>
                        <div className="size-matrix-bars">
                            {row.target_distribution.map(td => (
                                <div key={td.size} className="size-bar-row">
                                    <span className="size-bar-label">{td.size}</span>
                                    <div className="size-bar-track">
                                        <div className={`size-bar-fill ${row.is_user_size ? 'highlight' : ''}`}
                                            style={{ width: `${td.percent}%` }} />
                                    </div>
                                    <span className="size-bar-pct">{td.percent}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div className="size-guide-page">
            <div className="size-guide-header">
                <h1>Size Reference</h1>
                <p>Sourced from the community. Sizes and fits may vary.</p>
            </div>

            {/* ─── Selection area ─── */}
            <div className="size-guide-selection">

                <div className="size-guide-panel">
                    <div className="size-panel-label">Brand I want to buy</div>

                    {!targetBrand ? (
                        <div className="size-search-wrapper">
                            <input type="text" className="search-input" placeholder="Search a brand..."
                                value={targetSearch} onChange={e => setTargetSearch(e.target.value)} />
                            {targetResults.length > 0 && (
                                <div className="size-search-dropdown">
                                    {targetResults.map(b => (
                                        <div key={b.id} className="size-search-result" onClick={() => selectTarget(b)}>{b.name}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="size-target-selected">
                            <span className="size-target-name">{targetBrand.name}</span>
                            <button className="size-target-clear" onClick={() => { setTargetBrand(null); setTargetCategory(''); setKnownBrands([]); resetResults() }}>Change</button>
                        </div>
                    )}

                    {targetBrand && (
                        <>
                            <div className="size-panel-label" style={{ marginTop: '0.75rem' }}>Category</div>
                            <div className="size-category-pills">
                                {CATEGORIES.map(c => (
                                    <button key={c.value}
                                        className={`size-category-pill ${targetCategory === c.value ? 'active' : ''}`}
                                        onClick={() => handleCategoryChange(c.value)}
                                    >{c.label}</button>
                                ))}
                            </div>
                        </>
                    )}

                    {canCompare && (
                        <button className="size-compare-btn" onClick={handleCompare} disabled={loading}>
                            {loading ? 'Finding matches...' : 'Compare sizing'}
                        </button>
                    )}
                </div>

                {targetBrand && targetCategory && (
                    <div className="size-guide-panel">
                        <div className="size-panel-label">
                            Brands I know my size in
                            <span className="size-panel-count">{knownBrands.length}/{MAX_KNOWN_BRANDS}</span>
                        </div>

                        {knownBrands.map(k => (
                            <div key={k.brand.id} className="known-brand-row">
                                <div className="known-brand-header">
                                    <span className="known-brand-name">{k.brand.name}</span>
                                    <button className="known-brand-remove" onClick={() => removeKnownBrand(k.brand.id)}>×</button>
                                </div>
                                <div className="known-brand-size-input">
                                    {renderKnownSizeInput(k)}
                                </div>
                            </div>
                        ))}

                        {knownBrands.length < MAX_KNOWN_BRANDS && (
                            <div className="size-search-wrapper">
                                <input type="text" className="search-input" placeholder="Add a brand you own..."
                                    value={knownSearch} onChange={e => setKnownSearch(e.target.value)} />
                                {knownResults.length > 0 && (
                                    <div className="size-search-dropdown">
                                        {knownResults.map(b => (
                                            <div key={b.id} className="size-search-result" onClick={() => addKnownBrand(b)}>{b.name}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ─── Results area ─── */}
            {hasCompared && !loading && (
                <div className="size-guide-results">
                    <div className="size-results-divider">
                        <span>{CATEGORIES.find(c => c.value === targetCategory)?.label} sizing at {targetBrand?.name}</span>
                    </div>
                    <div className="size-results-list">
                        {results.map(result => (
                            <div key={result.known_brand_id} className="size-result-block">
                                {renderMatrix(result)}
                            </div>
                        ))}
                        {results.length > 0 && results.every(r => !r.has_data) && (
                            <div className="size-no-results">
                                <p>No community data yet for this combination.</p>
                                <p>Be the first — add your sizing for these brands above.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ─── My Sizing section ─── */}
            {user && (
                <div className="my-sizing-section">
                    <div className="size-results-divider">
                        <span>My sizing</span>
                    </div>
                    <p className="my-sizing-subtitle">Your sizing across all brands — search to find, add, or update.</p>

                    <MySizingSearch
                        allBrands={allBrands}
                        userSizes={userSizes}
                        onSelect={brand => setEditingSizeFor(brand)}
                    />

                    {Object.keys(userSizes).length > 0 && (
                        <div className="my-sizing-list">
                            {allBrands
                                .filter(b => userSizes[b.id])
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(brand => (
                                    <MySizingRow
                                        key={brand.id}
                                        brand={brand}
                                        sizes={userSizes[brand.id]}
                                        onUpdate={() => setEditingSizeFor(brand)}
                                        onRemove={async () => {
                                            await deleteBrandSize(user.id, brand.id)
                                            reloadUserSizes()
                                        }}
                                    />
                                ))}
                        </div>
                    )}

                    {Object.keys(userSizes).length === 0 && (
                        <div className="my-sizing-empty">
                            No sizing added yet. Search for a brand above to get started.
                        </div>
                    )}
                </div>
            )}

            {editingSizeFor && (
                <SizeModal
                    brand={editingSizeFor}
                    existingSize={userSizes[editingSizeFor.id]}
                    onClose={() => setEditingSizeFor(null)}
                    onSaved={() => { reloadUserSizes(); setEditingSizeFor(null) }}
                />
            )}
        </div>
    )
}

export default SizeGuide
